import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import RewardsService from "../../../services/rewards/rewards.service";
import TransactionDialog from "./TransactionDialog";
import {
  UserRewardDetailsResponse,
} from "../../../models/rewards-model";
import TokenService from "../../../services/autentication/token.service";
import Helpers from "../../../utils/helpers";
import "./RewardDetails.scss";
import "../admin/RewardsAdminDashboard.scss";

const RewardDetails: React.FC = () => {
  const [rewardData, setRewardData] = useState<UserRewardDetailsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [rewardToCurrencyRatio, setRewardToCurrencyRatio] = useState<number>(0);
  const toast = React.useRef<Toast>(null);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [dialogTransactions, setDialogTransactions] = useState<any[]>([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [transactionTotalRecords, setTransactionTotalRecords] = useState(0);
  const [transactionPageNumber, setTransactionPageNumber] = useState(1);
  const [transactionPageSize, setTransactionPageSize] = useState(10);
  const [transactionFirst, setTransactionFirst] = useState(0);
  const [txSearchInput, setTxSearchInput] = useState("");
  const [txSearchQuery, setTxSearchQuery] = useState<string | null>(null);
  const [transactionSortField, setTransactionSortField] = useState<string>("transactionDate");
  const [transactionSortOrder, setTransactionSortOrder] = useState<1 | -1>(-1);

  const transactionSortOptions: { label: string; sortField: string; sortOrder: 1 | -1 }[] = [
    { label: "Date (Newest First)", sortField: "transactionDate", sortOrder: -1 },
    { label: "Date (Oldest First)", sortField: "transactionDate", sortOrder: 1 }
  ];
  const selectedTxSortOption = transactionSortOptions.find(
    o => o.sortField === transactionSortField && o.sortOrder === transactionSortOrder
  ) || transactionSortOptions[0];

  const fetchRewardDetails = async () => {
    try {
      setLoading(true);
      const userDetails = TokenService.getUser();
      const userId = userDetails?.userID || null;
      const data = await RewardsService.getUserRewardDetails(userId);
      setRewardData(data);
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch reward details",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewardDetails();
    fetchTransactions(1, transactionPageSize, null, transactionSortField, transactionSortOrder);
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    });
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString("en-IN");
  };

  const fetchTransactions = async (
    page: number = 1,
    size: number = 10,
    searchTxt: string | null = null,
    sortBy: string = "transactionDate",
    sortOrder: 1 | -1 = -1
  ) => {
    setTransactionLoading(true);
    setShowTransactionDialog(true);
    try {
      const userDetails = TokenService.getUser();
      const userId = userDetails?.userID || null;
      if (userId) {
        const response = await RewardsService.getUserRewardTransactions({
          userId,
          pageNumber: page,
          pageSize: size,
          searchText: searchTxt ?? undefined,
          sortBy,
          sortOrder: sortOrder === 1 ? "ASC" : "DESC",
        });
        setDialogTransactions(response.data);
        setTransactionTotalRecords(response.totalRecords);
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load transactions",
        life: 3000,
      });
    } finally {
      setTransactionLoading(false);
    }
  };

  const onTransactionPageChange = (event: PaginatorPageChangeEvent) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setTransactionFirst(event.first);
    setTransactionPageNumber(newPage);
    setTransactionPageSize(event.rows);
    fetchTransactions(newPage, event.rows, txSearchQuery, transactionSortField, transactionSortOrder);
  };

  const onTransactionSortChange = (option: any) => {
    if (!option) return;
    setTransactionSortField(option.sortField);
    setTransactionSortOrder(option.sortOrder as 1 | -1);
    setTransactionFirst(0);
    setTransactionPageNumber(1);
    fetchTransactions(1, transactionPageSize, txSearchQuery, option.sortField, option.sortOrder as 1 | -1);
  };

  const onTxSearch = () => {
    const trimmed = txSearchInput.trim();
    if (!trimmed) {
      onTxSearchClear();
      return;
    }
    setTxSearchQuery(trimmed);
    setTransactionFirst(0);
    setTransactionPageNumber(1);
    fetchTransactions(1, transactionPageSize, trimmed, transactionSortField, transactionSortOrder);
  };

  const onTxSearchClear = () => {
    setTxSearchInput("");
    setTxSearchQuery(null);
    setTransactionFirst(0);
    setTransactionPageNumber(1);
    fetchTransactions(1, transactionPageSize, null, transactionSortField, transactionSortOrder);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
      </div>
    );
  }

  const metrics = rewardData?.metrics || {
    UserID: 0,
    Name: '',
    PhoneNumber: '',
    TotalEarned: 0,
    TotalExpired: 0,
    TotalRedeemed: 0,
    TotalAvailable: 0,
    RefferedCount: 0,
    RewardToCurrencyRatio: 0,
  };

  return (
    <div className="reward-details-container rewards-admin-dashboard">
      <Toast ref={toast} />

      <div className="rewardsHeaderWrapper">
        <div className="rewardsHeaderIcon">
          <i className="pi pi-gift"></i>
        </div>
        <div>
          <h1 className="rewardsHeaderTitle">My Rewards</h1>
          <p className="rewardsHeaderSubtitle">Track your reward points and transaction history</p>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="rewardsMetricsGrid">
        <div className="rewardsMetricCard rewardsMetricAvailable">
          <div className="rewardsMetricContent">
            <div className="rewardsMetricIcon">
              <i className="pi pi-wallet"></i>
            </div>
            <div className="rewardsMetricInfo">
              <span className="rewardsMetricLabel">Available Points</span>
              {metrics.RewardToCurrencyRatio > 0 ? (
                <>
                  <span className="rewardsMetricValue text-green" style={{ color: '#16a34a' }}>
                    {Helpers.formatAmountInINR(Math.round(metrics.TotalAvailable * metrics.RewardToCurrencyRatio))}
                  </span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#6b7280', marginTop: '2px' }}>
                    {metrics.TotalAvailable} pts
                  </span>
                </>
              ) : (
                <span className="rewardsMetricValue">{metrics.TotalAvailable} pts</span>
              )}
            </div>
          </div>
        </div>

        <div className="rewardsMetricCard rewardsMetricCredited">
          <div className="rewardsMetricContent">
            <div className="rewardsMetricIcon">
              <i className="pi pi-arrow-up-right"></i>
            </div>
            <div className="rewardsMetricInfo">
              <span className="rewardsMetricLabel">Total Earned</span>
              {metrics.RewardToCurrencyRatio > 0 ? (
                <>
                  <span className="rewardsMetricValue text-green" style={{ color: '#16a34a' }}>
                    {Helpers.formatAmountInINR(Math.round(metrics.TotalEarned * metrics.RewardToCurrencyRatio))}
                  </span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#6b7280', marginTop: '2px' }}>
                    {metrics.TotalEarned} pts
                  </span>
                </>
              ) : (
                <span className="rewardsMetricValue">{metrics.TotalEarned} pts</span>
              )}
            </div>
          </div>
        </div>

        <div className="rewardsMetricCard rewardsMetricRedeemed">
          <div className="rewardsMetricContent">
            <div className="rewardsMetricIcon">
              <i className="pi pi-shopping-cart"></i>
            </div>
            <div className="rewardsMetricInfo">
              <span className="rewardsMetricLabel">Total Redeemed</span>
              {metrics.RewardToCurrencyRatio > 0 ? (
                <>
                  <span className="rewardsMetricValue text-green" style={{ color: '#16a34a' }}>
                    {Helpers.formatAmountInINR(Math.round(metrics.TotalRedeemed * metrics.RewardToCurrencyRatio))}
                  </span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#6b7280', marginTop: '2px' }}>
                    {metrics.TotalRedeemed} pts
                  </span>
                </>
              ) : (
                <span className="rewardsMetricValue">{metrics.TotalRedeemed} pts</span>
              )}
            </div>
          </div>
        </div>

        <div className="rewardsMetricCard rewardsMetricExpired">
          <div className="rewardsMetricContent">
            <div className="rewardsMetricIcon">
              <i className="pi pi-clock"></i>
            </div>
            <div className="rewardsMetricInfo">
              <span className="rewardsMetricLabel">Expired Points</span>
              {metrics.RewardToCurrencyRatio > 0 ? (
                <>
                  <span className="rewardsMetricValue text-green" style={{ color: '#16a34a' }}>
                    {Helpers.formatAmountInINR(Math.round(metrics.TotalExpired * metrics.RewardToCurrencyRatio))}
                  </span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#6b7280', marginTop: '2px' }}>
                    {metrics.TotalExpired} pts
                  </span>
                </>
              ) : (
                <span className="rewardsMetricValue">{metrics.TotalExpired} pts</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Logs Section */}
      <Card className="transactions-card">
        <div className="transactions-header">
          <h3>Transaction History</h3>
        </div>

        {/* Instead of just a button, render the whole component here */}
        <div className="mt-4">
          <TransactionDialog
            isInline={true}
            visible={true} // Not used as a dialog anymore
            onHide={() => { }}
            userName={metrics.Name}
            transactions={dialogTransactions}
            isLoading={transactionLoading}
            totalRecords={transactionTotalRecords}
            first={transactionFirst}
            rows={transactionPageSize}
            onPageChange={onTransactionPageChange}
            rowsPerPageOptions={[5, 10, 20, 50]}
            searchInput={txSearchInput}
            searchQuery={txSearchQuery}
            onSearchInputChange={setTxSearchInput}
            onSearch={onTxSearch}
            onSearchClear={onTxSearchClear}
            sortOptions={transactionSortOptions}
            selectedSortOption={selectedTxSortOption}
            onSortChange={onTransactionSortChange}
            formatNumber={formatNumber}
            formatDate={formatDate}
            formatDateTime={formatDateTime}
          />
        </div>
      </Card>
    </div>
  );
};

export default RewardDetails;
